import { useMemo } from 'react'

import * as Sentry from '@sentry/browser'
import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import { ReadSyncValidationsQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type SyncIssue = {
  title: string
  description: string
  buttonLabel?: string
  buttonUrl?: string
  onAction?: () => void
  affectedNames?: string[]
}

export type IssuesByKeyParams = {
  integrationName?: string
  datoId?: string | null
  companyId?: string
}

export type IssuesByKeyFactory = (params: IssuesByKeyParams) => Record<string, SyncIssue>

type SyncValidationResult = NonNullable<
  ReadSyncValidationsQuery['integrations']['syncValidationsConnection']['nodes'][0]
>['issues'][0]

const resolveIssues = (
  validationResults: SyncValidationResult[],
  issuesByKey: Record<string, SyncIssue>,
  marketplaceIntegration: MarketplaceDbIntegration | null,
  isValidation: boolean
): SyncIssue[] => {
  const knownIssues: SyncIssue[] = []
  let hasUnknownIssue = false

  for (const result of validationResults) {
    const issue = issuesByKey[result.errorMessage.key]

    if (issue) {
      knownIssues.push({
        title: issue.title,
        description: issue.description,
        buttonLabel: issue.buttonLabel,
        buttonUrl: issue.buttonUrl,
        onAction: issue.onAction,
        affectedNames: result.affectedNames,
      })
    } else if (isValidation && result.errorMessage.value) {
      knownIssues.push({
        title: result.errorMessage.key,
        description: result.errorMessage.value,
        affectedNames: result.affectedNames,
      })
    } else {
      Sentry.captureException(
        new Error(`Unknown sync validation key: "${result.errorMessage.key}"`),
        {
          tags: {
            integration: marketplaceIntegration?.name ?? 'unknown',
            syncValidationKey: result.errorMessage.key,
          },
          extra: {
            errorValue: result.errorMessage.value,
            affectedNames: result.affectedNames,
          },
        }
      )
      hasUnknownIssue = true
    }
  }

  if (hasUnknownIssue) {
    knownIssues.push({
      title: i18n.t('integrations.unknown_sync_error.title'),
      description: i18n.t('integrations.unknown_sync_error.description', {
        integration_name: marketplaceIntegration?.displayName,
      }),
    })
  }

  return knownIssues
}

export const useSyncIssues = (
  validationResults: SyncValidationResult[],
  marketplaceIntegration: MarketplaceDbIntegration | null,
  issuesByKeyFactory: IssuesByKeyFactory,
  isValidation: boolean = true,
  companyId?: string
): SyncIssue[] => {
  const issuesByKey = issuesByKeyFactory({
    integrationName: marketplaceIntegration?.displayName,
    datoId: marketplaceIntegration?.datoId,
    companyId,
  })

  return resolveIssues(validationResults, issuesByKey, marketplaceIntegration, isValidation)
}

export const useIssuesWithModalClose = (issues: SyncIssue[], closeModal: () => void) => {
  return useMemo(
    () =>
      issues.map((issue) => {
        if (!issue.onAction) return issue
        const { onAction } = issue
        return {
          ...issue,
          onAction: () => {
            onAction()
            closeModal()
          },
        }
      }),
    [issues, closeModal]
  )
}
